import React, { useEffect, useMemo, useState } from 'react';
import { ProtectedRoute } from '../../../components/ProtectedRoute';
import { AsyncBoundary } from '../../../components/AsyncBoundary';
import { CustomerStatCard, CustomerSearch, CustomerTable, EditCustomerModal } from './components';
import {
  CustomerStat,
  CustomerItem,
  TierType,
  HealthType,
} from './customersTypes';
import { useCustomersStore, useCustomerSummaryStore } from '../../../store/customersStore';
import {
  Customer,
  mapTierLabel,
  deriveHealth,
} from '../../../services/customersApi';
import styles from './AdminCustomers.module.css';

const statIcons = [
  <svg viewBox="0 0 24 24" fill="currentColor"><path d="M11.8 10.9c-2.27-.59-3-1.2-3-2.15 0-1.09 1.01-1.85 2.7-1.85 1.78 0 2.44.85 2.5 2.1h2.21c-.07-1.72-1.12-3.3-3.21-3.81V3h-3v2.16c-1.94.42-3.5 1.68-3.5 3.61 0 2.31 1.91 3.46 4.7 4.13 2.5.6 3 1.48 3 2.41 0 .69-.49 1.79-2.7 1.79-2.06 0-2.87-.92-2.98-2.1h-2.2c.12 2.19 1.76 3.42 3.68 3.83V21h3v-2.15c1.95-.37 3.5-1.5 3.5-3.55 0-2.84-2.43-3.81-4.7-4.4z"/></svg>,
  <svg viewBox="0 0 24 24" fill="currentColor"><path d="M16 6l2.29 2.29-4.88 4.88-4-4L2 16.59 3.41 18l6-6 4 4 6.3-6.29L22 12V6z"/></svg>,
  <svg viewBox="0 0 24 24" fill="currentColor"><path d="M11.8 10.9c-2.27-.59-3-1.2-3-2.15 0-1.09 1.01-1.85 2.7-1.85 1.78 0 2.44.85 2.5 2.1h2.21c-.07-1.72-1.12-3.3-3.21-3.81V3h-3v2.16c-1.94.42-3.5 1.68-3.5 3.61 0 2.31 1.91 3.46 4.7 4.13 2.5.6 3 1.48 3 2.41 0 .69-.49 1.79-2.7 1.79-2.06 0-2.87-.92-2.98-2.1h-2.2c.12 2.19 1.76 3.42 3.68 3.83V21h3v-2.15c1.95-.37 3.5-1.5 3.5-3.55 0-2.84-2.43-3.81-4.7-4.4z"/></svg>,
  <svg viewBox="0 0 24 24" fill="currentColor"><path d="M16 6l2.29 2.29-4.88 4.88-4-4L2 16.59 3.41 18l6-6 4 4 6.3-6.29L22 12V6z"/></svg>,
];

function currency(n: number): string {
  if (Math.abs(n) >= 1000) {
    return `$${Math.round(n).toLocaleString()}`;
  }
  return `$${n.toFixed(0)}`;
}

function adaptToItem(c: Customer): CustomerItem {
  const tier = mapTierLabel(c.pricing_tier_name) as TierType;
  const health = deriveHealth(c) as HealthType;
  const orderCount = c.metrics.order_count;
  // Score is a coarse 0-100 derived from health + revenue, used purely for display.
  const score = Math.min(
    100,
    Math.round(
      (health === 'Green' ? 70 : health === 'Yellow' ? 50 : 30) +
        Math.min(30, c.metrics.total_revenue / 5000)
    )
  );
  const trend: 'up' | 'down' = health === 'Red' ? 'down' : 'up';
  // marginPercent isn't on backend yet — surface a neutral 0 so the column
  // continues to render but doesn't show fabricated numbers.
  return {
    id: c.id,
    name: c.store_name || c.full_name,
    location: [c.city, c.state].filter(Boolean).join(', ') || '—',
    tier,
    score,
    scoreTrend: trend,
    revenue: c.metrics.total_revenue,
    marginPercent: 0,
    ordersPerWeek: orderCount > 0 ? +(orderCount / 4).toFixed(1) : 0,
    aov: c.metrics.avg_order_value,
    health,
    claims: 0,
  };
}

function buildStats(summary: ReturnType<typeof useCustomerSummaryStore.getState>['summary']): CustomerStat[] {
  if (!summary) return [];
  return [
    {
      label: 'Total Customers',
      value: String(summary.total),
      change: `${summary.active} active`,
      iconBg: '#EFF6FF',
      iconColor: '#2563EB',
    },
    {
      label: 'Weekly Revenue',
      value: currency(summary.weekly_revenue),
      change: 'Last 7 days',
      iconBg: '#ECFDF5',
      iconColor: '#059669',
    },
    {
      label: 'Avg Order Value',
      value: currency(summary.avg_order_value),
      change: 'All-time',
      iconBg: '#EFF6FF',
      iconColor: '#2563EB',
    },
    {
      label: 'Suspended',
      value: String(summary.suspended),
      change: `${summary.inactive} inactive`,
      iconBg: '#FEF2F2',
      iconColor: '#DC2626',
    },
  ];
}

const AdminCustomers: React.FC = () => {
  const [search, setSearch] = useState('');
  const [activeTier, setActiveTier] = useState('All');

  const items = useCustomersStore((s) => s.items);
  const loading = useCustomersStore((s) => s.loading);
  const error = useCustomersStore((s) => s.error);
  const fetchList = useCustomersStore((s) => s.fetchList);
  const [editing, setEditing] = useState<Customer | null>(null);

  const summary = useCustomerSummaryStore((s) => s.summary);
  const fetchSummary = useCustomerSummaryStore((s) => s.fetchSummary);

  useEffect(() => {
    fetchList().catch(() => undefined);
    fetchSummary().catch(() => undefined);
  }, [fetchList, fetchSummary]);

  const adapted = useMemo<CustomerItem[]>(() => items.map(adaptToItem), [items]);

  const filteredItems = useMemo(() => {
    let list = adapted;
    if (activeTier !== 'All') {
      list = list.filter((c) => c.tier === activeTier);
    }
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(
        (c) => c.name.toLowerCase().includes(q) || c.location.toLowerCase().includes(q)
      );
    }
    return list;
  }, [adapted, search, activeTier]);

  const stats = useMemo(() => buildStats(summary), [summary]);

  return (
    <div className={styles.container}>
      {/* Stat Cards */}
      <div className={styles.statsGrid}>
        {stats.map((stat, i) => (
          <CustomerStatCard key={stat.label} stat={stat} icon={statIcons[i]} />
        ))}
      </div>

      {/* Search + Tier Filter */}
      <CustomerSearch
        value={search}
        onChange={setSearch}
        activeTier={activeTier}
        onTierChange={setActiveTier}
      />

      {/* Table */}
      <AsyncBoundary
        loading={loading && items.length === 0}
        error={error}
        empty={!loading && filteredItems.length === 0}
        emptyMessage="No customers match the current filters"
        onRetry={() => fetchList()}
      >
        <CustomerTable
          items={filteredItems}
          onEdit={(id) => {
            const c = items.find((x) => x.id === id);
            if (c) setEditing(c);
          }}
        />
      </AsyncBoundary>

      <EditCustomerModal open={!!editing} customer={editing} onClose={() => setEditing(null)} />
    </div>
  );
};

export const AdminCustomersPage: React.FC = () => (
  <ProtectedRoute requiredRoles={['admin', 'Sales Rep']}>
    <AdminCustomers />
  </ProtectedRoute>
);

export { AdminCustomers };
export default AdminCustomers;
